import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin'

import { DataGrist, FetchOption, GristRecord } from '@operato/data-grist'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { PopupHandle, openPopup } from '@operato/layout'
import { PageView, store } from '@operato/shell'
import { CommonButtonStyles, CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice } from '@operato/utils'

import './data-archive-request-popup.js'

@customElement('data-archive-list-page')
export class DataArchiveListPage extends connect(store)(localize(i18next)(PageView)) {
  static styles = [
    ScrollbarStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        overflow: hidden;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() gristConfig: any
  @state() mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'

  @query('ox-grist') private grist!: DataGrist

  private popup?: PopupHandle

  get context() {
    return {
      title: i18next.t('title.data-archive list'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-archive',
      actions: [
        {
          title: i18next.t('button.request-archive'),
          action: this.openArchivePopup.bind(this),
          icon: 'archive'
        }
      ],
      exportable: {
        name: i18next.t('title.data-archive list'),
        data: this._exportableData.bind(this)
      },
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'LIST' : 'GRID')

    return html`
      <ox-grist
        .mode=${mode}
        .config=${this.gristConfig}
        .fetchHandler=${this.fetchHandler.bind(this)}
        ?url-params-sensitive=${false /* this.active */}
      >
        <div slot="headroom" class="header">
          <!-- <div class="filters">
            <ox-filters-form class="filter" autofocus without-search></ox-filters-form>
          </div> -->

          <ox-context-page-toolbar class="actions" .context=${this.context}></ox-context-page-toolbar>
        </div>
      </ox-grist>
    `
  }

  async pageInitialized(lifecycle) {
    this.gristConfig = {
      list: { fields: ['updater', 'updatedAt'] },
      columns: [
        { type: 'gutter', gutterName: 'sequence' },
        // { type: 'gutter', gutterName: 'row-selector', multiple: true },
        {
          type: 'resource-object',
          name: 'creator',
          header: i18next.t('field.creator'),
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'datetime',
          name: 'createdAt',
          header: i18next.t('field.created_at'),
          sortable: true,
          width: 180,
          imex: true
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          sortable: true,
          width: 180,
          imex: true
        },
        {
          type: 'json5',
          name: 'requestParams',
          header: i18next.t('field.request-params'),
          record: {
            editable: false
          },
          width: 200,
          imex: true
        },
        {
          type: 'string',
          name: 'downloadUrl',
          label: true,
          header: i18next.t('field.download-url'),
          record: {
            editable: false
          },
          width: 240,
          imex: true
        },
        {
          type: 'string',
          name: 'status',
          label: true,
          header: i18next.t('field.status'),
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        }
      ],
      rows: {
        appendable: false,
        selectable: {
          multiple: true
        }
      },
      sorters: [
        {
          name: 'createdAt',
          desc: true
        }
      ]
    }
  }

  closePopupAndRefesh() {
    this.popup?.close()
    this.grist.fetch()
  }

  async openArchivePopup() {
    this.popup = openPopup(
      html` <data-archive-request-popup
        @requested=${this.closePopupAndRefesh.bind(this)}
        @created=${this.closePopupAndRefesh.bind(this)}
      ></data-archive-request-popup>`,
      {
        backdrop: true,
        size: 'small',
        title: i18next.t('title.data-archive request popup')
      }
    )
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    const response = await client.query({
      query: gql`
        query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          responses: dataArchives(filters: $filters, pagination: $pagination, sortings: $sortings) {
            items {
              id
              type
              requestParams
              downloadUrl
              status
              creator {
                id
                name
              }
              updater {
                id
                name
              }
              updatedAt
              createdAt
            }
            total
          }
        }
      `,
      variables: {
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }

  _exportableData() {
    let records = [] as GristRecord[]
    if (this.grist.selected && this.grist.selected.length > 0) {
      records = this.grist.selected
    } else {
      records = this.grist.data.records
    }

    var headerSetting = this.grist.compiledConfig.columns
      .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
      .map(column => {
        return column.imex === true
          ? {
              header: column.header.renderer(column),
              key: column.name,
              width: column.width,
              type: column.type
            }
          : column.imex
      })

    var data = records.map(item => {
      return {
        id: item.id,
        ...this.gristConfig.columns
          .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
          .reduce((record, column) => {
            const key = column.imex === true ? column.name : column.imex.key
            record[key] = key
              .split('.')
              .reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), item)
            return record
          }, {})
      }
    })

    return { header: headerSetting, data: data }
  }
}
